import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faPenToSquare, faTrash, faXmark, faArrowLeft, faEye } from '@fortawesome/free-solid-svg-icons';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import useAutoDismissSnack from '../../../Hooks/AutoDismissSnack';

function ControlVehicles() {
  const { id_torre } = useParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [parkings, setParkings] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewVehicle, setViewVehicle] = useState(null);

  // Form states
  const [placa, setPlaca] = useState("");
  const [modelo, setModelo] = useState("");
  const [marca, setMarca] = useState("");
  const [telefono, setTelefono] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedParking, setSelectedParking] = useState("");

  // Snack notifications
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState("");
  const [snackMessage, setSnackMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    getVehicles();
  }, [id_torre]);

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
  };
  useAutoDismissSnack(openSnack, setOpenSnack);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getVehicles = () => {
    axios.get(`${BASE_URL}/vehicles/tower/${id_torre}`)
      .then(response => {
        setVehicles(response.data);
        setFilteredVehicles(response.data);
      })
      .catch(error => console.error('Error fetching vehicles:', error));
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = vehicles.filter((vehicle) =>
      vehicle.placa.toLowerCase().includes(value) ||
      vehicle.marca.toLowerCase().includes(value) ||
      vehicle.modelo.toLowerCase().includes(value) ||
      vehicle.usuario_nombre.toLowerCase().includes(value) ||
      vehicle.usuario_apellido.toLowerCase().includes(value) ||
      vehicle.num_apartamento.toString().includes(value)
    );
    setFilteredVehicles(filtered);
    setPage(0);
  };

  const handleClickOpen = () => {
    setOpen(true);
    // Cargar usuarios e inquilinos de la torre
    axios.get(`${BASE_URL}/inquilinos/${id_torre}`)
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users:', error));
    
    // Cargar parqueaderos disponibles
    axios.get(`${BASE_URL}/parkings/available/${id_torre}`)
      .then(response => setParkings(response.data))
      .catch(error => console.error('Error fetching parkings:', error));
  };

  const handleClose = () => {
    setOpen(false);
    setPlaca("");
    setModelo("");
    setMarca("");
    setTelefono("");
    setSelectedUser("");
    setSelectedParking("");
  };

  const handleAddSubmit = (event) => {
    event.preventDefault();

    const vehicleData = {
      placa: placa,
      modelo: modelo,
      marca: marca,
      telefono: telefono,
      id_usuario: selectedUser,
      id_parqueadero: selectedParking
    };

    axios.post(`${BASE_URL}/addVehicle`, vehicleData)
      .then(response => {
        if (response.status === 200) {
          setSnackStatus("success");
          setSnackMessage("Vehículo agregado correctamente");
          getVehicles();
          handleClose();
        }
        handleClickSnack();
      })
      .catch(error => {
        console.error('Error adding vehicle:', error);
        setSnackStatus("error");
        setSnackMessage(error.response?.data?.error || "Error al agregar el vehículo");
        handleClickSnack();
      });
  };

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditOpen(true);
    
    // Cargar usuarios
    axios.get(`${BASE_URL}/inquilinos/${id_torre}`)
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users:', error));
    
    // Cargar parqueaderos disponibles (incluyendo el actual)
    axios.get(`${BASE_URL}/parkings/tower/${id_torre}`)
      .then(response => {
        // Filtrar solo disponibles o el actual
        const available = response.data.filter(p => 
          p.estado === 'Disponible' || p.id_parqueadero === vehicle.id_parqueadero
        );
        setParkings(available);
      })
      .catch(error => console.error('Error fetching parkings:', error));
  };

  const handleEditSubmit = (event) => {
    event.preventDefault();

    const vehicleData = {
      placa: selectedVehicle.placa,
      modelo: selectedVehicle.modelo,
      marca: selectedVehicle.marca,
      telefono: selectedVehicle.telefono,
      id_usuario: selectedVehicle.id_usuario,
      id_parqueadero: selectedVehicle.id_parqueadero
    };

    axios.put(`${BASE_URL}/updateVehicle/${selectedVehicle.id_vehiculo}`, vehicleData)
      .then(response => {
        if (response.status === 200) {
          setSnackStatus("success");
          setSnackMessage("Vehículo actualizado correctamente");
          getVehicles();
          setEditOpen(false);
        }
        handleClickSnack();
      })
      .catch(error => {
        console.error('Error updating vehicle:', error);
        setSnackStatus("error");
        setSnackMessage(error.response?.data?.error || "Error al actualizar el vehículo");
        handleClickSnack();
      });
  };

  const handleDeleteVehicle = (id_vehiculo) => {
    if (window.confirm('¿Está seguro de eliminar este vehículo?')) {
      axios.delete(`${BASE_URL}/deleteVehicle/${id_vehiculo}`)
        .then(response => {
          if (response.status === 200) {
            setSnackStatus("success");
            setSnackMessage("Vehículo eliminado correctamente");
            getVehicles();
          }
          handleClickSnack();
        })
        .catch(error => {
          console.error('Error deleting vehicle:', error);
          setSnackStatus("error");
          setSnackMessage("Error al eliminar el vehículo");
          handleClickSnack();
        });
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/adminVehicles')}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
            Volver
          </button>

          {/* Snackbar Alert */}
          {openSnack && (
            <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
              snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white animate-fade-in-down`} style={{ zIndex: 9999 }}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{snackMessage}</span>
                <button onClick={handleCloseSnack} className="ml-2 hover:bg-white/20 rounded p-1 transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <FontAwesomeIcon icon={faTruck} className="h-10 w-10 text-lime-600" />
              <h1 className="text-4xl font-bold text-gray-800">Vehículos de la Torre {id_torre}</h1>
            </div>

            {/* Search and Add Button */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
              <div className="flex-1 w-full sm:max-w-md">
                <input
                  type="text"
                  placeholder="Buscar por placa, marca, modelo, propietario o apartamento..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="input-field w-full"
                />
              </div>
              <button
                onClick={handleClickOpen}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
              >
                <FontAwesomeIcon icon={faTruck} className="h-5 w-5" />
                Agregar Vehículo
              </button>
            </div>
          </div>

          {/* Modal para agregar vehículo */}
          {open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                  <h2 className="text-2xl font-bold text-gray-800">Agregar Vehículo</h2>
                  <p className="text-gray-600 mt-1">Ingrese los datos del vehículo</p>
                </div>
                <form onSubmit={handleAddSubmit}>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Placa *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={placa}
                          onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                          required
                          autoFocus
                          placeholder="ABC-123"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Marca *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={marca}
                          onChange={(e) => setMarca(e.target.value.toUpperCase())}
                          required
                          placeholder="Toyota, Chevrolet, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Modelo *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={modelo}
                          onChange={(e) => setModelo(e.target.value.toUpperCase())}
                          required
                          placeholder="Corolla, Spark, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Propietario *
                        </label>
                        <select
                          className="input-field"
                          value={selectedUser}
                          onChange={(e) => {
                            setSelectedUser(e.target.value);
                            const user = users.find(u => u.id_usuario === parseInt(e.target.value));
                            if (user && user.telefono) {
                              setTelefono(user.telefono);
                            }
                          }}
                          required
                        >
                          <option value="">Seleccione un propietario</option>
                          {users.map((user) => (
                            <option key={user.id_usuario} value={user.id_usuario}>
                              {user.nombre} {user.apellido} - Apto {user.num_apartamento}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono del Propietario
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                          placeholder="3001234567"
                          maxLength="20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parqueadero *
                        </label>
                        <select
                          className="input-field"
                          value={selectedParking}
                          onChange={(e) => setSelectedParking(e.target.value)}
                          required
                        >
                          <option value="">Seleccione un parqueadero</option>
                          {parkings.map((parking) => (
                            <option key={parking.id_parqueadero} value={parking.id_parqueadero}>
                              Parqueadero {parking.num_parqueadero} - {parking.torre_nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Agregar Vehículo
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal para editar vehículo */}
          {editOpen && selectedVehicle && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                  <h2 className="text-2xl font-bold text-gray-800">Editar Vehículo</h2>
                  <p className="text-gray-600 mt-1">Modifique los datos del vehículo</p>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Placa *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={selectedVehicle.placa}
                          onChange={(e) => setSelectedVehicle({...selectedVehicle, placa: e.target.value.toUpperCase()})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Marca *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={selectedVehicle.marca}
                          onChange={(e) => setSelectedVehicle({...selectedVehicle, marca: e.target.value.toUpperCase()})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Modelo *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={selectedVehicle.modelo}
                          onChange={(e) => setSelectedVehicle({...selectedVehicle, modelo: e.target.value.toUpperCase()})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Propietario *
                        </label>
                        <select
                          className="input-field"
                          value={selectedVehicle.id_usuario}
                          onChange={(e) => {
                            setSelectedVehicle({...selectedVehicle, id_usuario: e.target.value});
                            const user = users.find(u => u.id_usuario === parseInt(e.target.value));
                            if (user && user.telefono) {
                              setSelectedVehicle({...selectedVehicle, id_usuario: e.target.value, telefono: user.telefono});
                            }
                          }}
                          required
                        >
                          <option value="">Seleccione un propietario</option>
                          {users.map((user) => (
                            <option key={user.id_usuario} value={user.id_usuario}>
                              {user.nombre} {user.apellido} - Apto {user.num_apartamento}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono del Propietario
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={selectedVehicle.telefono || ''}
                          onChange={(e) => setSelectedVehicle({...selectedVehicle, telefono: e.target.value})}
                          placeholder="3001234567"
                          maxLength="20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parqueadero *
                        </label>
                        <select
                          className="input-field"
                          value={selectedVehicle.id_parqueadero}
                          onChange={(e) => setSelectedVehicle({...selectedVehicle, id_parqueadero: e.target.value})}
                          required
                        >
                          <option value="">Seleccione un parqueadero</option>
                          {parkings.map((parking) => (
                            <option key={parking.id_parqueadero} value={parking.id_parqueadero}>
                              Parqueadero {parking.num_parqueadero} - {parking.torre_nombre || 'Disponible'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setEditOpen(false)}
                      className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Guardar Cambios
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-lime-500 to-green-600">
                  <tr>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Placa</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Marca</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Modelo</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Teléfono</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Propietario</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Apartamento</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Parqueadero</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVehicles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((vehicle) => (
                      <tr key={vehicle.id_vehiculo} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">{vehicle.placa}</td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">{vehicle.marca}</td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">{vehicle.modelo}</td>
                        <td className="px-6 py-4 text-center text-sm text-blue-600 font-medium">
                          {vehicle.telefono || '-'}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-900">
                          {vehicle.usuario_nombre} {vehicle.usuario_apellido}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                          {vehicle.num_apartamento}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {vehicle.num_parqueadero ? `#${vehicle.num_parqueadero}` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => { setViewVehicle(vehicle); setViewOpen(true); }}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
                              title="Ver vehículo"
                            >
                              <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
                              Ver
                            </button>
                            <button
                              onClick={() => handleEditVehicle(vehicle)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                              title="Editar vehículo"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} className="h-5 w-5" />
                              Editar
                            </button>
                            <button
                              onClick={() => handleDeleteVehicle(vehicle.id_vehiculo)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm hover:shadow-md"
                              title="Eliminar vehículo"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Filas por página:</span>
                <select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  className="input-field py-1 px-2 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredVehicles.length)} de {filteredVehicles.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => handleChangePage(e, page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={(e) => handleChangePage(e, page + 1)}
                    disabled={page >= Math.ceil(filteredVehicles.length / rowsPerPage) - 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* View Vehicle Dialog */}
      {viewOpen && viewVehicle && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-lime-500 to-green-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Detalle del Vehículo</h2>
              <button onClick={() => setViewOpen(false)} className="text-white/80 hover:text-white transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Foto */}
              <div className="flex justify-center mb-5">
                {viewVehicle.foto ? (
                  <img
                    src={`${BASE_URL}${viewVehicle.foto}`}
                    alt={viewVehicle.placa}
                    className="w-40 h-40 rounded-2xl object-cover shadow-md border-4 border-lime-100"
                  />
                ) : (
                  <div className="w-40 h-40 rounded-2xl bg-lime-50 border-4 border-lime-100 flex items-center justify-center">
                    <FontAwesomeIcon icon={faTruck} className="h-20 w-20 text-lime-300" />
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="space-y-3">
                {[
                  { label: 'Placa', value: viewVehicle.placa },
                  { label: 'Marca', value: viewVehicle.marca },
                  { label: 'Modelo', value: viewVehicle.modelo },
                  { label: 'Propietario', value: `${viewVehicle.usuario_nombre} ${viewVehicle.usuario_apellido}` },
                  { label: 'Apartamento', value: viewVehicle.num_apartamento },
                  { label: 'Parqueadero', value: viewVehicle.num_parqueadero ? `#${viewVehicle.num_parqueadero}` : '—' },
                  { label: 'Teléfono', value: viewVehicle.telefono || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-500 w-28 shrink-0">{label}:</span>
                    <span className="text-sm text-gray-800 font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setViewOpen(false)}
                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ControlVehicles;

